import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Dropdown, Input, Popup, SemanticICONS } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import { useDebounce } from 'use-lodash-debounce';
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import TableComponent from '~/components/TableComponent'
import PreviewModal from './PreviewModal'
import CustomIcon from '~/components/CustomIcon'
import { handleErrors } from '~/utils/helpers'
import { DownloadService } from '~/api/services/downloadFiles'
import i18n from '~/components/i18n'
import { Nullable } from '~/types'
import CO2Service, { FarmerCo2Type } from '~/api/services/co2'
import ResetModal from '../ShowCO2Climate/ResetModal'
import { SURVEY_STATUS_KEYS } from '~/Constants'
import notify from '~/components/Notify'
import RecalculateModal from '../ShowCO2Climate/RecalculateModal'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    type: 'farmerLink',
    sorted: 'desc'
  },
  {
    name: i18n.t('Progress'),
    key: 'percentage',
    type: 'clima-progress',
    sorted: 'desc'
  },
  {
    name: i18n.t('Status'),
    key: 'completed',
    type: 'verified'
  },
  {
    name: i18n.t('Last login'),
    key: 'lastLogin',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Completed at'),
    key: 'completedAt',
    type: 'date',
    format: 'dddd, D MMMM YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Emission factor'),
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'number-or-value',
    unit: 'kg CO2e / kg FPCM',
    decimalScale: 2,
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const ResultsCO2Climate: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const [farmerId, setFarmerId] = useState(null)
  const [uniqueId, setUniqueId] = useState(null)
  const [resetFarmerData, setResetFarmerData] = useState<Nullable<FarmerCo2Type>>(null)
  const [recalculateFarmerData, setRecalculateFarmerData] = useState<Nullable<FarmerCo2Type>>(null)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    completed: null
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const completedFilterOpt = [
    {
      text: t('Data collection completed'),
      value: 1
    },
    {
      text: t('Data collection not completed'),
      value: 0
    }
  ]

  const actions = [
    {
      popupMessage: t('Show'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'show',
      verifiedActionKey: 'completed',
      verifiedAction: 1
    },
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />,
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'pdf-results',
      renderActions: getActions
    },
    {
      popupMessage: 'Datenerhebung zurücksetzen',
      type: 'icon',
      name: 'repeat',
      size: 'large',
      actionType: 'reset',
      renderActions: getActions
    },
    {
      popupMessage: 'Datenerhebung. neu berechnen',
      type: 'icon',
      name: 'sync',
      size: 'large',
      actionType: 'recalculate',
      renderActions: getActions
    }
  ]

  const {
    isLoading: isBusyData,
    data: farmers,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getFarmers(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    }
  );

  const {
    isLoading: isBusyShow,
    data: showData
  } = CO2ClimateQuery.getCO2ShowData(id);

  useEffect(() => {
    fetchFilteredData()
  }, [t])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  async function handleDownloadPdfResults(farmer) {
    setLoadingAction({
      type: 'pdf-results',
      id: farmer.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`co2-footprints/${id}/farmers/${farmer?.id}/get-results/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function getActions(farmer, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    const isLive = showData?.status === SURVEY_STATUS_KEYS.live
    const displayResetAction = isLive && farmer.percentage > 0
    const displayRecalculateAction = farmer?.completed === 1
    switch (actionType) {
      case 'pdf-results':
        return farmer?.completed === 1 && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handleDownloadPdfResults(farmer)}
            size='large'
          />}
        />
      case 'reset':
        return displayResetAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setResetFarmerData(farmer)}
            size='large'
          />}
        />
      case 'recalculate':
        return displayRecalculateAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setRecalculateFarmerData(farmer)}
            size='large'
          />}
        />
      default:
        return
    }
  }

  function renderSearch() {
    return <>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selectOnBlur={false}
        options={completedFilterOpt}
        disabled={isBusyData || isBusyShow}
        style={{ marginLeft: 10 }}
        selection
        placeholder={t('Data collection completed?')}
        clearable
        value={dataFiltered.completed}
        onChange={(e, data) => {
          setDataFiltered({
            ...dataFiltered,
            //@ts-ignore per backend request
            completed: isNaN(data.value) ? null : data.value
          })
        }}
      />
    </>
  }

  function handleShow(row) {
    setFarmerId(row?.id)
    setUniqueId(row?.uniqueId)
  }

  async function handleActionClick(row, type) {
    switch (type) {
      case 'show':
        return handleShow(row)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      actions={actions}
      isFetchingData={isFetchingData}
      loadingAction={loadingAction}
      animated
      handleClickAction={(row, type) => handleActionClick(row, type)}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setDataFiltered(data)
        setTableColumnHeaders(tableColumnHeaders)
      }}
      rows={rows}
      total={filtered}
    />
  }

  async function handleRecalculateData() {
    setLoadingAction({
      type: 'recalculate',
      id: recalculateFarmerData.id,
      value: true,
      idKey: 'id'
    })
    try {
      await CO2Service.recalculateFarmersCo2Slot(id, recalculateFarmerData.id)
      fetchFilteredData()
      notify('Die Datenerfassung wurde erfolgreich neu berechnet.', 'success')

    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function renderPreviewModal() {
    return <PreviewModal
      farmerId={farmerId}
      handleClose={() => {
        setFarmerId(null)
        setUniqueId(null)
      }}
      uniqueId={uniqueId}
    />
  }

  function renderResetModal() {
    return <ResetModal
      handleClose={(submited?: boolean) => {
        setResetFarmerData(null)
        if (submited) {
          fetchFilteredData()
        }
      }}
      farmersData={resetFarmerData}
    />
  }

  function renderRecalculateModal() {
    return <RecalculateModal
      handleClose={(submited?: boolean) => {
        setRecalculateFarmerData(null)
        if (submited) {
          handleRecalculateData()
        }
      }}
      farmersData={recalculateFarmerData}
    />
  }

  const { filtered, rows } = farmers ? farmers : { filtered: 0, rows: [] };

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: t('Results CO2 Klima check') }} />}
    segmented
    isBusy={isBusyData || isBusyShow}
    isCo2Loader={true}
    filters={renderSearch()}
    close={RouteTypes.CO2_CLIMATE}
  >
    {renderRecalculateModal()}
    {renderResetModal()}
    {renderTable()}
    {renderPreviewModal()}
  </Layout>
}
export default ResultsCO2Climate